import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEO, { buildBreadcrumbSchema } from '@/components/SEO';
import FAQHero from './components/FAQHero';
import FAQAccordion from './components/FAQAccordion';


const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const faqCategories = [
  {
    category: 'Getting Started',
    items: [
      {
        question: 'How is Fore Capital different from other payment processors?',
        answer: 'Most processors sell you a rate and disappear. We stay embedded — auditing your current setup, building custom infrastructure, and optimizing continuously. You get full visibility into every fee, real-time reporting, and a partner who actually answers the phone. Unlike traditional processors, we do not lock you into opaque pricing or long-term contracts with hidden exit fees.',
      },
      {
        question: 'What does the free analysis include?',
        answer: 'We review your last 3 months of processing statements, identify hidden fees and markups, benchmark your rates against industry standards, and deliver a clear report showing exactly how much you are overpaying — with no obligation. The analysis covers interchange fees, assessment fees, processor markups, monthly minimums, PCI fees, and any other line items on your statement.',
      },
      {
        question: 'How long does implementation take?',
        answer: 'Most clients are fully integrated within 30 to 60 days. Complex multi-channel setups may take longer, but we stay hands-on throughout and never hand you off to a support queue. We assign a dedicated implementation engineer to your account from day one through go-live.',
      },
      {
        question: 'Is there a minimum processing volume to work with Fore Capital?',
        answer: 'We work best with businesses processing $500K or more annually. Below that threshold, the savings from our optimization work may not justify the engagement. If you are below that level today but growing quickly, reach out — we are happy to have a conversation about your trajectory.',
      },
    ],
  },
  {
    category: 'Switching & Migration',
    items: [
      {
        question: 'Will switching processors disrupt my business?',
        answer: 'No. We design the transition to run parallel with your existing system until everything is tested and confirmed. Your customers never see downtime, and your cash flow stays uninterrupted. We have migrated hundreds of merchants and have a proven playbook for zero-disruption cutover.',
      },
      {
        question: 'What happens to my existing contracts and equipment?',
        answer: 'We review your current contracts and identify any early termination fees before you commit to anything. In many cases, the savings from switching more than offset any exit costs. For hardware, we can often reprogram existing terminals or provide new equipment at no upfront cost depending on your processing volume.',
      },
      {
        question: 'How do you handle data migration from my current processor?',
        answer: 'We work directly with your current processor to migrate transaction history, customer vault data, and recurring billing schedules. Tokenized card data is migrated using network tokenization standards so your customers never need to re-enter their payment information.',
      },
      {
        question: 'Can I keep my current POS system?',
        answer: 'In most cases, yes. We integrate with all major POS platforms including Square, Toast, Lightspeed, Clover, and hundreds of others. If your POS is not on our standard integration list, our engineering team will build a custom connector.',
      },
    ],
  },
  {
    category: 'Pricing & Fees',
    items: [
      {
        question: 'How does your pricing work?',
        answer: 'We use interchange-plus pricing, which means you pay the actual interchange rate set by Visa and Mastercard plus a small, fixed markup. This is the most transparent pricing model in the industry. You will always know exactly what you are paying and why — no bundled rates, no tiered pricing, no surprises.',
      },
      {
        question: 'Are there monthly fees or minimums?',
        answer: 'We keep monthly fees minimal and fully disclosed upfront. There are no hidden monthly minimums, no PCI non-compliance fees if you stay current, and no annual fees. Any recurring charges are itemized clearly on your monthly statement.',
      },
      {
        question: 'How much can I realistically save by switching?',
        answer: 'Most merchants switching from a traditional processor save between 15% and 40% on their total processing costs. The exact amount depends on your current rates, your card mix, and your transaction volume. Our free analysis will give you a precise number before you commit to anything.',
      },
    ],
  },
  {
    category: 'Technology & Integration',
    items: [
      {
        question: 'What industries do you specialize in?',
        answer: 'We focus on retail, hospitality, e-commerce, and enterprise — businesses with complex payment flows, multiple channels, and high transaction volumes. If you process more than $500K annually, we are built for you. We have deep expertise in high-ticket retail, multi-location restaurant groups, subscription businesses, and B2B invoice payments.',
      },
      {
        question: 'Do you support international payments?',
        answer: 'Yes. Our infrastructure supports multi-currency processing, cross-border payouts, and compliance with regional regulations across North America, Europe, and Asia-Pacific. We support over 135 currencies and can settle in 30+ currencies directly to your bank account.',
      },
      {
        question: 'What developer tools do you offer?',
        answer: 'We provide a RESTful API, webhooks, SDKs for iOS, Android, JavaScript, Python, Ruby, PHP, and Go, plus a sandbox environment for testing. Our API documentation is comprehensive and our developer support team responds within one business day. We also offer pre-built integrations for Shopify, WooCommerce, Magento, Salesforce, and NetSuite.',
      },
      {
        question: 'How does your fraud prevention work?',
        answer: 'Our fraud prevention layer uses machine learning models trained on billions of transactions to score each payment in real time. We combine device fingerprinting, behavioral analytics, velocity checks, and 3D Secure 2.0 to block fraud before it happens — without adding friction for legitimate customers. Clients typically see a 60–80% reduction in chargebacks within the first 90 days.',
      },
    ],
  },
  {
    category: 'Security & Compliance',
    items: [
      {
        question: 'Are you PCI DSS compliant?',
        answer: 'Yes. We are a PCI DSS Level 1 certified service provider — the highest level of certification in the payment industry. We undergo annual audits by a Qualified Security Assessor (QSA) and quarterly network scans. Working with us significantly reduces your own PCI compliance burden.',
      },
      {
        question: 'How do you protect cardholder data?',
        answer: 'We use point-to-point encryption (P2PE) at the terminal level so card data is never in your environment in the clear. All stored card data is tokenized using network tokens issued by Visa and Mastercard. Our infrastructure is hosted in SOC 2 Type II certified data centers with 99.99% uptime SLAs.',
      },
      {
        question: 'What happens if there is a data breach?',
        answer: 'Because of our P2PE and tokenization architecture, a breach of your systems would not expose any usable cardholder data. In the unlikely event of a breach at our level, we carry comprehensive cyber liability insurance and have a dedicated incident response team. We handle all regulatory notifications and work directly with card brands on your behalf.',
      },
    ],
  },
];

const faqSchemaItems = faqCategories.flatMap(cat =>
  cat.items.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  }))
);

const pageSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSchemaItems,
  },
  buildBreadcrumbSchema([
    { name: 'Home', href: '/' },
    { name: 'FAQ', href: '/faq' },
  ]),
];

export { faqCategories };

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-[#F9F9F7]" style={{ fontFamily: "'Google Sans Flex', sans-serif" }}>
      <SEO
        title="FAQ | Fore Capital | Payment Infrastructure Questions Answered"
        description="Answers to the most common questions about Fore Capital's payment infrastructure, pricing, migration, security, and developer integrations."
        keywords="payment processing FAQ, merchant services questions, payment gateway FAQ, switching processors, PCI compliance"
        pathname="/faq"
        schema={pageSchema}
      />
      <Navbar />
      <main>
        <FAQHero />
        <FAQAccordion categories={faqCategories} />
      </main>
      <Footer />
    </div>
  );
}